"use client";

import { Switch } from "@/shadcnComponents/ui/switch";
import { Button } from "@/shadcnComponents/ui/button";
import { Briefcase, BarChart2, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { usePayment } from "@/Providers/Payment";
import { useGeneralSettings } from "@/Providers/General";
import { useAuth } from "@/Providers/Auth";
import { useInfluencer } from "@/Providers/Influencer";
import MobileMenuBar from "@/Components/Menubar/MobileMenuBar";

type SubscriptionPlan = {
  _id: string;
  planType: string;
  planName: string;
  planNumberOfDays: number;
  planMaxAmount: { $numberDecimal: string };
  planNetAmount: { $numberDecimal: string };
  planFreeCredits: number;
  planStatus: number;
  planIsFree: boolean;
  planPerCreditCharge: number;
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  __v: number;
  collabCharges: number;
  collabCommission: number;
};

export default function InfluencerActivatePage() {
  const [openForCollaboration, setOpenForCollaboration] = useState(true);

  const [amountToPay, setAmountToPay] = useState(0);
  const [AllPlans, setAllPlans] = useState<SubscriptionPlan[]>([]);
  const paymentState = usePayment();
  const generalState = useGeneralSettings();
  const authState = useAuth();
  const influencerState = useInfluencer();
  const [, setPaymentProcessing] = useState(false);

  useEffect(() => {
    const getAllPlan = async () => {
      generalState.setLoading(true);
      const result = await influencerState?.getSubscriptionPlans();
      if (result?.success) {
        generalState?.setLoading(false);
        setAllPlans(result?.AllPlans);
      } else {
        generalState?.setLoading(false);
        generalState?.showErrorToast("Something went wrong");
      }
    };
    getAllPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    setAmountToPay(parseInt(AllPlans?.[0]?.planNetAmount?.$numberDecimal));
  }, [AllPlans]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePaymentResponse = async (response: any, orderId: string) => {
    setPaymentProcessing(true);
    generalState?.setLoading(false);

    try {
      switch (response.status) {
        // 1. user cancelled the payment mode
        case "userCancelled":
          setPaymentProcessing(false);
          generalState?.setLoading(false);
          alert(
            "It is a paid service, for using it you have to pay the one time payment"
          );
          break;

        //2. payment dropping by user --- dropped the payment by the user
        case "dropped":
          setPaymentProcessing(false);
          generalState?.setLoading(false);
          alert(
            "It is a paid service, for using it you have to pay the one time payment"
          );
          break;

        //  2. payment failed due to any reason
        case "failure":
          setPaymentProcessing(false);
          generalState?.setLoading(false);
          await paymentState?.updateInfluencerOrder(orderId, response);
          alert(
            "Payment Failed, if amount got deducted inform us at info@anchors.in"
          );

          break;

        //  3. Payment pending due to any reason
        case "pending":
          // Inform lark bot about the failure
          setPaymentProcessing(false);
          generalState?.setLoading(false);
          await paymentState?.updateInfluencerOrder(orderId, response);
          alert(
            "Payment is still pending, complete the payment to proceed,for issues inform us at info@anchors.in"
          );
          break;

        // 4. success payment
        case "success": {
          setPaymentProcessing(false);
          generalState?.setLoading(false);

          const result = await paymentState?.updateInfluencerOrder(
            orderId,
            response
          );
          await influencerState?.updatePersonalInfo({
            openForCollaboration,
          });
          if (result?.success) {
            window.open("/influencer/activationSuccess", "_self");
          } else {
            throw new Error("Some error occurred");
          }
          break;
        }

        // Else all cases  -----------------
        default:
          setPaymentProcessing(false);
          generalState?.setLoading(false);

          alert(
            "The order is not placed. Try again!!! ,in case of issues inform us at info@anchors.in "
          );
          break;
      }
    } catch (error) {
      alert(
        "The order is not placed. Try again!!! ,in case of issues inform us at info@anchors.in "
      );
    }
  };

  const handleActivateClick = async () => {
    generalState?.setLoading(true);
    const currentSelectedPlan = AllPlans[0];

    try {
      const order = await paymentState?.createInfluencerEasebuzzOrder(
        "InfluencerFirstPay",
        currentSelectedPlan?.planNetAmount.$numberDecimal,
        authState?.loggedUser?.name,
        authState?.loggedUser?.email,
        "Joining Fees",
        Date.now() +
          currentSelectedPlan?.planNumberOfDays * 24 * 60 * 60 * 1000,
        authState?.loggedUser?.mobile,
        undefined,
        currentSelectedPlan?._id
      );

      if (amountToPay > 0) {
        const key = await paymentState?.getEaseBuzz();

        const easebuzzCheckout = new window.EasebuzzCheckout(key, "prod");

        const options = {
          access_key: order?.paymentData.data,
          onResponse: (response) => {
            // handling the edge cases of the response
            handlePaymentResponse(response, order?.order ?? "");
          },
          theme: "#000000", // color hex
        };

        easebuzzCheckout.initiatePayment(options);
      } else {
        await influencerState?.updatePersonalInfo({
          openForCollaboration,
        });
        window.open("/influencer/activationSuccess", "_self");
      }
    } catch (error) {
      console.log(error);
      alert("Error in processing the payment");
      setPaymentProcessing(false);
      generalState?.setLoading(false);
    }
  };

  const handleToggleChange = (checked: boolean) => {
    if (!checked) {
      // Only show confirmation when turning off
      setOpenForCollaboration(false);
    } else {
      setOpenForCollaboration(true);
    }
  };

  return (
    <>
      <MobileMenuBar />
      <div className=" bg-[#1C1C1C] text-white p-8 flex items-center justify-center">
        <div className="max-w-xl w-full space-y-8">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-medium text-gray-200">
              activate Profile
            </h1>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Your profile will be able to see to brand only if you are profile
              is active, you can toggle off whenever you are not open for collab
            </p>
          </div>

          <div className="space-y-4">
            <FeatureCard
              icon={Briefcase}
              bgColor="bg-[#E9967A]"
              title="Public Media Kit"
              description="Your professional portfolio, accessible anywhere"
            />
            <FeatureCard
              icon={BarChart2}
              bgColor="bg-[#40E0D0]"
              title="Profile Analysis"
              description="Deep insights into your content performance"
            />
            <FeatureCard
              icon={Users}
              bgColor="bg-[#FF69B4]"
              title="Invite-Only Community"
              description="Connect with top-tier creators"
            />

            <div className="bg-[#1E1E1E] rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-[#9370DB] rounded-lg flex items-center justify-center">
                    <UserIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-gray-200 text-sm font-medium">
                      open for collaborations
                    </h3>
                    <p className="text-gray-400 text-xs">
                      Let brands discover and work with you
                    </p>
                  </div>
                </div>
                <Switch
                  checked={openForCollaboration}
                  onCheckedChange={handleToggleChange}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-center pt-4">
            <Button
              onClick={handleActivateClick}
              className="bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg px-12 py-3 w-full"
            >
              Activate Profile
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

function FeatureCard({ icon: Icon, bgColor, title, description }) {
  return (
    <div className="bg-[#1E1E1E] rounded-lg p-4 border border-gray-800">
      <div className="flex items-center space-x-4">
        <div
          className={`w-10 h-10 ${bgColor} rounded-lg flex items-center justify-center`}
        >
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-gray-200 text-sm font-medium">{title}</h3>
          <p className="text-gray-400 text-xs">{description}</p>
        </div>
      </div>
    </div>
  );
}

function UserIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
